import BScroll from '@better-scroll/core';
import { BScrollConstructor } from '@better-scroll/core/dist/types/BScroll';
import Slide from '@better-scroll/slide';
import { AppBar, Box, Grid, Stack, Tab, Tabs } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import BaseImage from '../../../../components/common/baseImage';
import { AudioPlayContext } from '../../../../contexts/AudioPlayContext';
import { VideoPlayerContext } from '../../../../contexts/VideoContext';
import { useECard } from '../../../../hooks/useECard';
import { TableInfoKeyType, useTableInfo } from '../../../../hooks/useTableInfo';
import { BetType } from '../../../../models/games/dragonTiger/BetType';
import { GameType } from '../../../../models/games/enums/GameType';
import { RootState } from '../../../../store/store';
import { ExchangeBetAmount } from '../../../../utils/commonFunc';
import { limitRedFormatter } from '../../../../utils/formatter';
import { getHostById } from '../../../host/slice';
import { InGameRoadMap } from '../../../lobby/components/Roadmaps';
import { REPORT_COLORS } from '../../../lobby/constants';
import { getMainLimitRedState, getMainUserState } from '../../../main/selector';
import { DragonTigerECard } from '../ECard/DragonTigerECard';
import './DragonTigerTableInfoPanel.scss';
import { getMaxBetByBetType } from './util';

export const DragonTigerTableInfoPanel = () => {
    const { hostId } = useContext(VideoPlayerContext);
    const { playButtonAudio } = useContext(AudioPlayContext);
    const { tableInfo, setTableInfo } = useTableInfo();
    const { t } = useTranslation();
    const show = useECard(hostId);
    const [tableInfoTab, setTableInfoTab] = useState<number>(0);
    const ref = useRef<HTMLDivElement>(null);
    const [scrollObj, setScrollObj] = useState<BScrollConstructor>();
    const handleChange = (v: number) => {
        playButtonAudio();
        if (scrollObj) {
            if (v !== tableInfoTab) {
                setTableInfoTab(v);
                scrollObj.goToPage(v, 0);
            }
        }
    };
    useEffect(() => {
        if (ref && ref.current) {
            let numLsTab = 0;
            if (tableInfo === 'markerRoad') {
                numLsTab = 1;
            } else if (tableInfo === 'tableInfo') {
                numLsTab = 2;
            }
            setTableInfoTab(numLsTab);
            BScroll.use(Slide);
            const newBScroll = new BScroll(ref.current as HTMLDivElement, {
                scrollX: true,
                scrollY: false,
                click: true,
                slide: {
                    loop: false,
                    autoplay: false,
                    threshold: 100,
                    startPageXIndex: numLsTab,
                },
                momentum: false,
            });
            newBScroll.on('slidePageChanged', (page: { pageX: number }) => {
                if (page) {
                    const tab = page.pageX;
                    setTableInfoTab(tab);
                    let gKey: TableInfoKeyType = 'roadmap';
                    if (tab === 1) {
                        gKey = 'markerRoad';
                    } else if (tab == 2) {
                        gKey = 'tableInfo';
                    }
                    setTableInfo(gKey);
                }
            });
            setScrollObj(newBScroll);
        }
        return () => {
            if (ref && ref.current && scrollObj) {
                scrollObj.destroy();
            }
        };
    }, [ref]);
    return (
        <>
            <Box className="table-info dragon-tiger">
                <AppBar>
                    <Tabs
                        className="table-info-tabs"
                        value={tableInfoTab}
                        onChange={(_, v) => {
                            handleChange(v);
                        }}
                        aria-label="roadmap & table info."
                        sx={{ '& Button': { color: REPORT_COLORS.TITLE_TEXT } }}
                    >
                        <Tab
                            className="table-info-tab0"
                            label={t('road_map')}
                        />
                        <Tab
                            className="table-info-tab1"
                            label={t('marker_map')}
                        />
                        <Tab
                            className="table-info-tab2"
                            label={t('tableinfo.name')}
                        />
                    </Tabs>
                </AppBar>
                <Box
                    ref={ref}
                    sx={{
                        overflow: 'hidden',
                        zIndex: 1,
                    }}
                >
                    <Box sx={{ display: 'inline-block' }}>
                        <Box
                            sx={{
                                display: 'inline-block',
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            <InGameRoadMap
                                hostId={hostId}
                                gameType={GameType.Dragon}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: 'inline-block',
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            <InGameRoadMap
                                hostId={hostId}
                                isMarker={true}
                                gameType={GameType.Dragon}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: 'inline-block',
                                scrollSnapAlign: 'start',
                                overflow: 'hidden',
                            }}
                        >
                            <DragonTigerTableInfoTab />
                        </Box>
                    </Box>
                </Box>
                <DragonTigerECard show={show} />
            </Box>
        </>
    );
};

type InfoProps = {
    title: string;
    value: string | number;
};
type TableInfoTypeRow1 = {
    table: InfoProps;
    dealer: InfoProps;
    round: InfoProps;
    id: InfoProps;
};
type TableInfoTypeRow2 = {
    betLimit: InfoProps;
    tableLimit: InfoProps;
    maxLimit: InfoProps;
    minLimit: InfoProps;
};
type TableInfoTypeRow3 = {
    dragon: InfoProps;
    tiger: InfoProps;
    tie: InfoProps;
};
type TableInfoType = [TableInfoTypeRow1, TableInfoTypeRow2, TableInfoTypeRow3];
const initTableInfoData = [
    {
        table: {
            title: 'tableinfo.table',
            value: '',
        },
        dealer: {
            title: 'tableinfo.dealer',
            value: '',
        },
        round: {
            title: 'tableinfo.shoe',
            value: '',
        },
        id: {
            title: 'tableinfo.round',
            value: '',
        },
    },
    {
        betLimit: {
            title: 'betLimit',
            value: '',
        },
        tableLimit: {
            title: 'tableinfo.tablelimit',
            value: '',
        },
        maxLimit: {
            title: 'tableinfo.maxlimit',
            value: '',
        },
        minLimit: {
            title: 'tableinfo.minlimit',
            value: '',
        },
    },
    {
        dragon: {
            title: 'tableinfo.dragon',
            value: '',
        },
        tiger: {
            title: 'tableinfo.tiger',
            value: '',
        },
        tie: {
            title: 'betlog.bet_bacc.0',
            value: '',
        },
    },
] as TableInfoType;
const DragonTigerTableInfoTab = () => {
    const { t } = useTranslation();
    const { hostId } = useContext(VideoPlayerContext);
    const host = useSelector((state: RootState) => getHostById(state, hostId));
    const { CurrentResult, DealerList, TableLimit, GameType, MaxBet } = host;
    const { ExchangeRate, CurrencyName } = useSelector((state: RootState) =>
        getMainUserState(state)
    );
    const limitRedByGameType = useSelector(getMainLimitRedState);
    const [tableInfo, setTableInfo] =
        useState<TableInfoType>(initTableInfoData);

    useEffect(() => {
        if (MaxBet) {
            const newTableInfo: TableInfoType = Object.create(
                tableInfo ?? initTableInfoData
            );
            //row 3
            newTableInfo[2].tie.value = getMaxBetByBetType(
                BetType.DTBTie,
                MaxBet
            );
            newTableInfo[2].dragon.value = getMaxBetByBetType(
                BetType.DTBDragonWin,
                MaxBet
            );
            newTableInfo[2].tiger.value = getMaxBetByBetType(
                BetType.DTBTigerWin,
                MaxBet
            );
            setTableInfo(newTableInfo);
        }
    }, [MaxBet]);
    useEffect(() => {
        if (
            hostId &&
            DealerList &&
            TableLimit &&
            CurrentResult &&
            limitRedByGameType.BetRuleByGameType
        ) {
            const newTableInfo: TableInfoType = Object.create(
                tableInfo ?? initTableInfoData
            );
            //row 1
            newTableInfo[0].table.value = t(`hostCodeNames.${hostId}`);
            newTableInfo[0].dealer.value = DealerList[0].DealerName;
            const shoes = Math.floor(CurrentResult.GameCount / 10000);
            const round = CurrentResult.GameCount % 10000;
            const row1Round = shoes && round ? `${shoes}-${round}` : round;
            newTableInfo[0].round.value = row1Round;
            newTableInfo[0].id.value = CurrentResult.GameID || 0;
            //row 2
            let betLimitV = '';
            const limitRedByGame = limitRedByGameType.BetRuleByGameType?.find(
                br => br.GameType === GameType
            );
            const BetRule = limitRedByGame?.BetRule;
            let selectedBetRule = BetRule?.find(r => r.Selected === 1);
            if (!selectedBetRule && BetRule) selectedBetRule = BetRule[0]; //有機會全部selected=0
            if (selectedBetRule) {
                betLimitV = limitRedFormatter(selectedBetRule, CurrencyName);
            }
            newTableInfo[1].betLimit.value = betLimitV;
            let infoTableLimit =
                ExchangeBetAmount(TableLimit, ExchangeRate) / 100;
            const digi = Math.pow(10, infoTableLimit.toString().length - 1);
            const tableLimitValue = (
                Math.round(infoTableLimit / digi) * digi
            ).toLocaleString('en-US', {
                maximumFractionDigits: 2,
            });
            newTableInfo[1].tableLimit.value = tableLimitValue;
            if (selectedBetRule) {
                newTableInfo[1].maxLimit.value = (
                    selectedBetRule.LimitRed / 100
                ).toLocaleString('en-US', {
                    maximumFractionDigits: 2,
                });
                newTableInfo[1].minLimit.value = (
                    selectedBetRule.MinBet / 100
                ).toLocaleString('en-US', {
                    maximumFractionDigits: 2,
                });
            }
            setTableInfo(newTableInfo);
        }
    }, [
        hostId,
        CurrentResult,
        DealerList,
        TableLimit,
        limitRedByGameType.BetRuleByGameType,
    ]);
    return (
        <>
            {tableInfo && (
                <>
                    <Stack
                        direction={'row'}
                        justifyContent={'space-between'}
                        sx={{ fontSize: '14px', width: 540 }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                            }}
                        >
                            <BaseImage
                                className="tradbaccBg table_info_bg"
                                scale={1}
                                opacity={0.5}
                                // 18px = (196 - 161) / 2
                                // 1.226 = 196 / 161
                                transform={
                                    'translate(0, 17.5px) scale(1, 1.226)'
                                }
                            />
                        </Box>
                        {tableInfo.map((r, i) => {
                            const style =
                                i !== 1
                                    ? { width: 178.2, zIndex: '1' }
                                    : {
                                          width: 183.6,
                                          zIndex: '1',
                                          borderLeft:
                                              '2px solid rgba(255,255,255,0.2)',
                                          borderRight:
                                              '2px solid rgba(255,255,255,0.2)',
                                      };
                            return (
                                <Box key={`info-${i}`} sx={style}>
                                    <>
                                        {Object.keys(r).map((k, ii) => {
                                            const v = r[
                                                k as keyof typeof r
                                            ] as InfoProps;
                                            return (
                                                <Grid
                                                    key={`info-cell${ii}`}
                                                    item
                                                    container
                                                    justifyContent={
                                                        'space-between'
                                                    }
                                                    alignItems={'center'}
                                                    sx={{
                                                        height: '49.25px',
                                                        padding:
                                                            '0 7.5px 0 7.5px',
                                                    }}
                                                >
                                                    <Grid
                                                        item
                                                        sx={{
                                                            color: REPORT_COLORS.TITLE_TEXT,
                                                        }}
                                                    >
                                                        {t(v.title)}
                                                    </Grid>
                                                    <Grid item>{v.value}</Grid>
                                                </Grid>
                                            );
                                        })}
                                    </>
                                </Box>
                            );
                        })}
                    </Stack>
                </>
            )}
        </>
    );
};
